import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import {
    ArrowLeft, BarChart3, 
    CheckCircle2, Sliders, 
    Sparkles, RefreshCw,
    Zap,
    FileSpreadsheet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminMunicipalityAnalytics = () => {
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    useTranslation();
    const [municipalities, setMunicipalities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [conversionRate, setConversionRate] = useState(40); // Close rate %
    const [monthlySupportAvg, setMonthlySupportAvg] = useState(2500); // ₾ per month

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}municipalities.php`);
            const data = await res.json();
            if (Array.isArray(data)) {
                // Filter out occupied
                const active = data.filter(m => !m.is_occupied && !['akhalgori', 'azhara', 'eredvi', 'kurta', 'tighva'].includes(m.slug));
                setMunicipalities(active);
            }
        } catch (err) {
            console.error('Failed to load municipalities analytics', err);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (user) fetchData();
    }, [user]);

    // Categorization
    const stats = useMemo(() => {
        const green = municipalities.filter(m => ['contract_signed', 'supported_live'].includes(m.status));
        const yellow = municipalities.filter(m => ['negotiation', 'meeting_scheduled', 'in_development'].includes(m.status));
        const blue = municipalities.filter(m => m.status === 'offer_sent');
        const red = municipalities.filter(m => ['lead', 'rejected'].includes(m.status));

        const totalBudget = municipalities.reduce((acc, m) => acc + parseFloat(m.estimated_budget || 0), 0);
        const greenBudget = green.reduce((acc, m) => acc + parseFloat(m.estimated_budget || 0), 0);
        const yellowBudget = yellow.reduce((acc, m) => acc + parseFloat(m.estimated_budget || 0), 0);
        const blueBudget = blue.reduce((acc, m) => acc + parseFloat(m.estimated_budget || 0), 0);
        const redBudget = red.reduce((acc, m) => acc + parseFloat(m.estimated_budget || 0), 0);

        // Simulated Projected Revenue based on close rate
        const projectedPipelineRevenue = (greenBudget * 1.0) + (yellowBudget * 0.7) + (blueBudget * (conversionRate / 100)) + (redBudget * 0.15);
        const projectedAnnualSupport = (green.length + Math.round(yellow.length * 0.7) + Math.round(blue.length * (conversionRate / 100))) * monthlySupportAvg * 12;

        // Regional grouping
        const regionsMap = {};
        municipalities.forEach(m => {
            const reg = m.region_ka || m.region || 'სხვა';
            if (!regionsMap[reg]) {
                regionsMap[reg] = { name: reg, count: 0, budget: 0, contracted: 0 };
            }
            regionsMap[reg].count += 1;
            regionsMap[reg].budget += parseFloat(m.estimated_budget || 0);
            if (['contract_signed', 'supported_live'].includes(m.status)) {
                regionsMap[reg].contracted += 1;
            }
        });

        const regionalList = Object.values(regionsMap).sort((a, b) => b.budget - a.budget);

        // Service types
        const serviceMap = {
            create_new: { label: 'ახალი ვებსაიტი', count: 0 },
            redesign_update: { label: 'რედიზაინი', count: 0 },
            maintenance_support: { label: 'მხარდაჭერა', count: 0 },
            cybersecurity_audit: { label: 'კიბერ-აუდიტი', count: 0 },
            all_in_one: { label: 'სრული პაკეტი', count: 0 },
        };
        municipalities.forEach(m => {
            if (serviceMap[m.offer_type]) {
                serviceMap[m.offer_type].count += 1;
            }
        });

        return {
            total: municipalities.length,
            green,
            yellow,
            blue,
            red,
            totalBudget,
            greenBudget,
            yellowBudget,
            blueBudget,
            redBudget,
            projectedPipelineRevenue,
            projectedAnnualSupport,
            regionalList,
            serviceMap
        };
    }, [municipalities, conversionRate, monthlySupportAvg]);

    return (
        <AdminLayout title="მუნიციპალიტეტების ანალიტიკა და პროგნოზები">
            {/* Top Back & Header Bar */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <div className="d-flex align-items-center gap-3">
                    <button 
                        className="shadcn-btn shadcn-btn-secondary" 
                        onClick={() => navigate('/admin/municipalities')}
                    >
                        <ArrowLeft size={16} /> მუნიციპალიტეტების სია
                    </button>
                    <div>
                        <h4 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                            <span>მუნიციპალური ბაზრის AI პროგნოზი & ანალიტიკა</span>
                            <span className="badge bg-indigo text-white rounded-pill fs-7 px-3 py-1" style={{ background: '#6366f1' }}>
                                64 მუნიციპალიტეტი
                            </span>
                        </h4>
                        <span className="text-muted smallest">
                            საქართველოს არაოკუპირებული მუნიციპალიტეტების ციფრული ტრანსფორმაციის პოტენციალი და შემოსავლების მოდელი
                        </span>
                    </div>
                </div>

                <div className="d-flex gap-2">
                    <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={fetchData} disabled={loading}>
                        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> განახლება
                    </button>
                    <button 
                        className="shadcn-btn shadcn-btn-primary shadcn-btn-sm"
                        onClick={() => window.print()}
                    >
                        <FileSpreadsheet size={14} /> ანგარიშის ბეჭდვა
                    </button>
                </div>
            </div>

            {/* ── 4 CORE STATUS CARDS (SYNCED METRICS) ── */}
            <div className="row g-3 mb-4">
                {/* 1. GREEN: Contracted */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div 
                        className="shadcn-card p-3 h-100" 
                        style={{ 
                            borderLeft: '5px solid #16a34a',
                            borderTop: '1px solid #86efac',
                            borderRight: '1px solid #86efac',
                            borderBottom: '1px solid #86efac',
                            background: '#f0fdf4'
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="badge fw-bold px-2 py-1 rounded" style={{ background: '#dcfce7', color: '#14532d' }}>
                                🟢 გაფორმებული & ლაივ
                            </span>
                            <span className="smallest fw-semibold" style={{ color: '#166534' }}>100% ალბათობა</span>
                        </div>
                        <div className="fw-bold fs-3" style={{ color: '#000000' }}>{stats.green.length} <span className="fs-6 fw-normal" style={{ color: '#475569' }}>მუნიციპ.</span></div>
                        <div className="fw-bold small" style={{ color: '#15803d' }}>₾{stats.greenBudget.toLocaleString()} ბიუჯეტი</div>
                        <div className="smallest mt-1" style={{ color: '#334155' }}>კონტრაქტი გაფორმებულია, მიმდინარეობს მხარდაჭერა</div>
                    </div>
                </div>

                {/* 2. YELLOW: Working / In Progress */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div 
                        className="shadcn-card p-3 h-100" 
                        style={{ 
                            borderLeft: '5px solid #ca8a04',
                            borderTop: '1px solid #fde047',
                            borderRight: '1px solid #fde047',
                            borderBottom: '1px solid #fde047',
                            background: '#fefce8'
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="badge fw-bold px-2 py-1 rounded" style={{ background: '#fef08a', color: '#713f12' }}>
                                🟡 სამუშაო პროცესში
                            </span>
                            <span className="smallest fw-semibold" style={{ color: '#854d0e' }}>~70% ალბათობა</span>
                        </div>
                        <div className="fw-bold fs-3" style={{ color: '#000000' }}>{stats.yellow.length} <span className="fs-6 fw-normal" style={{ color: '#475569' }}>მუნიციპ.</span></div>
                        <div className="fw-bold small" style={{ color: '#a16207' }}>₾{stats.yellowBudget.toLocaleString()} ბიუჯეტი</div>
                        <div className="smallest mt-1" style={{ color: '#334155' }}>მოლაპარაკება, შეხვედრები, დეველოპმენტი</div>
                    </div>
                </div>

                {/* 3. BLUE: Offer Sent */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div 
                        className="shadcn-card p-3 h-100" 
                        style={{ 
                            borderLeft: '5px solid #2563eb',
                            borderTop: '1px solid #93c5fd',
                            borderRight: '1px solid #93c5fd',
                            borderBottom: '1px solid #93c5fd',
                            background: '#eff6ff'
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="badge fw-bold px-2 py-1 rounded" style={{ background: '#dbeafe', color: '#1e3a8a' }}>
                                🔵 შეთავაზება გაგზავნილია
                            </span>
                            <span className="smallest fw-semibold" style={{ color: '#1e40af' }}>~{conversionRate}% ალბათობა</span>
                        </div>
                        <div className="fw-bold fs-3" style={{ color: '#000000' }}>{stats.blue.length} <span className="fs-6 fw-normal" style={{ color: '#475569' }}>მუნიციპ.</span></div>
                        <div className="fw-bold small" style={{ color: '#1d4ed8' }}>₾{stats.blueBudget.toLocaleString()} ბიუჯეტი</div>
                        <div className="smallest mt-1" style={{ color: '#334155' }}>ტექნიკური წინადადება გაგზავნილია, პასუხის მოლოდინი</div>
                    </div>
                </div>

                {/* 4. LIGHT RED: Leads / No Communication */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div 
                        className="shadcn-card p-3 h-100" 
                        style={{ 
                            borderLeft: '5px solid #dc2626',
                            borderTop: '1px solid #fca5a5',
                            borderRight: '1px solid #fca5a5',
                            borderBottom: '1px solid #fca5a5',
                            background: '#fef2f2'
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="badge fw-bold px-2 py-1 rounded" style={{ background: '#fee2e2', color: '#7f1d1d' }}>
                                🔴 კომუნიკაციის გარეშე / ლიდები
                            </span>
                            <span className="smallest fw-semibold" style={{ color: '#991b1b' }}>საწყისი ეტაპი</span>
                        </div>
                        <div className="fw-bold fs-3" style={{ color: '#000000' }}>{stats.red.length} <span className="fs-6 fw-normal" style={{ color: '#475569' }}>მუნიციპ.</span></div>
                        <div className="fw-bold small" style={{ color: '#b91c1c' }}>₾{stats.redBudget.toLocaleString()} ბიუჯეტი</div>
                        <div className="smallest mt-1" style={{ color: '#334155' }}>საჭიროებს აუდიტს, პირველად კონტაქტსა და შეთავაზებას</div>
                    </div>
                </div>
            </div>

            {/* ── INTERACTIVE PREDICTION SIMULATOR ── */}
            <div className="shadcn-card p-4 mb-4 border-2" style={{ borderColor: 'rgba(99, 102, 241, 0.3)', background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.03), rgba(139, 92, 246, 0.03))' }}>
                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <div className="p-2 rounded bg-indigo text-white" style={{ background: '#6366f1' }}>
                                <Sliders size={18} />
                            </div>
                            <h5 className="fw-bold text-dark mb-0">ინტერაქტიული შემოსავლების პროგნოზირების სიმულატორი</h5>
                        </div>
                        <span className="text-muted small">
                            შეცვალეთ შეთავაზებების კონვერტაციის ალბათობა და ყოველთვიური მომსახურების ტარიფი, რათა მყისიერად ნახოთ წლიური პროგნოზი.
                        </span>
                    </div>

                    <div className="d-flex align-items-center gap-4 flex-wrap">
                        {/* Slider 1: Close Rate */}
                        <div className="bg-white p-3 rounded-3 border shadow-sm" style={{ minWidth: '220px' }}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="smallest text-muted fw-bold">კონვერტაციის ალბათობა:</span>
                                <span className="badge bg-primary rounded-pill">{conversionRate}%</span>
                            </div>
                            <input 
                                type="range" 
                                min="10" 
                                max="90" 
                                step="5"
                                value={conversionRate} 
                                onChange={e => setConversionRate(parseInt(e.target.value))}
                                className="form-range"
                            />
                        </div>

                        {/* Slider 2: Monthly Support */}
                        <div className="bg-white p-3 rounded-3 border shadow-sm" style={{ minWidth: '220px' }}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="smallest text-muted fw-bold">თვიური მხარდაჭერა / მუნიციპ.:</span>
                                <span className="badge bg-success rounded-pill">₾{monthlySupportAvg}</span>
                            </div>
                            <input 
                                type="range" 
                                min="1000" 
                                max="6000" 
                                step="250"
                                value={monthlySupportAvg} 
                                onChange={e => setMonthlySupportAvg(parseInt(e.target.value))}
                                className="form-range"
                            />
                        </div>
                    </div>
                </div>

                {/* Simulated Results Strip */}
                <div className="row g-3">
                    <div className="col-12 col-md-4">
                        <div className="p-3 bg-white rounded-3 border">
                            <span className="text-muted smallest d-block">სრული პოტენციური ბაზარი (64 მუნიციპ.)</span>
                            <span className="fw-bold fs-4 text-dark">₾{stats.totalBudget.toLocaleString()}</span>
                            <span className="smallest text-muted d-block mt-1">საწყისი ვებ-განვითარების პაკეტები</span>
                        </div>
                    </div>
                    <div className="col-12 col-md-4">
                        <div className="p-3 bg-white rounded-3 border border-primary border-opacity-50">
                            <span className="text-primary smallest fw-bold d-block">პროგნოზირებული შემოსავალი (შეთავაზებებიდან)</span>
                            <span className="fw-bold fs-4 text-primary">₾{Math.round(stats.projectedPipelineRevenue).toLocaleString()}</span>
                            <span className="smallest text-muted d-block mt-1">კონვერტაციის {conversionRate}% გათვლით</span>
                        </div>
                    </div>
                    <div className="col-12 col-md-4">
                        <div className="p-3 bg-white rounded-3 border border-success border-opacity-50">
                            <span className="text-success smallest fw-bold d-block">წლიური განმეორებადი შემოსავალი (MRR/ARR)</span>
                            <span className="fw-bold fs-4 text-success">₾{Math.round(stats.projectedAnnualSupport).toLocaleString()}</span>
                            <span className="smallest text-muted d-block mt-1">მხარდაჭერა, ჰოსტინგი & კიბერ-დაცვა (წლიური)</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── REGIONAL BREAKDOWN & SERVICE MATRIX ── */}
            <div className="row g-4 mb-4">
                {/* Regional Breakdown Table */}
                <div className="col-12 col-lg-7">
                    <div className="shadcn-card h-100">
                        <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                            <div className="shadcn-card-title">
                                <BarChart3 size={20} className="text-primary" />
                                <span>რეგიონალური ბაზრის ანალიტიკა (საქართველო)</span>
                            </div>
                            <span className="text-muted smallest">{stats.regionalList.length} რეგიონი</span>
                        </div>

                        <div className="table-responsive">
                            <table className="shadcn-table">
                                <thead>
                                    <tr>
                                        <th>რეგიონი</th>
                                        <th className="text-center">მუნიციპალიტეტები</th>
                                        <th className="text-center">კონტრაქტი</th>
                                        <th className="text-end">სავარაუდო ბიუჯეტი</th>
                                        <th className="text-end">წილი</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.regionalList.map((reg, idx) => {
                                        const pct = stats.totalBudget > 0 ? ((reg.budget / stats.totalBudget) * 100).toFixed(1) : 0;
                                        return (
                                            <tr key={idx}>
                                                <td className="fw-bold text-dark small">{reg.name}</td>
                                                <td className="text-center">
                                                    <span className="badge bg-light text-dark">{reg.count}</span>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge ${reg.contracted > 0 ? 'bg-success bg-opacity-20 text-success' : 'bg-light text-muted'}`}>
                                                        {reg.contracted}
                                                    </span>
                                                </td>
                                                <td className="text-end fw-semibold text-dark small">
                                                    ₾{reg.budget.toLocaleString()}
                                                </td>
                                                <td className="text-end">
                                                    <div className="d-flex align-items-center justify-content-end gap-2">
                                                        <span className="smallest text-muted">{pct}%</span>
                                                        <div className="progress" style={{ width: '50px', height: '6px' }}>
                                                            <div 
                                                                className="progress-bar bg-primary" 
                                                                style={{ width: `${pct}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Services Demand & Autonomous Readiness */}
                <div className="col-12 col-lg-5">
                    <div className="d-flex flex-column gap-4 h-100">
                        {/* Service Types Breakdown */}
                        <div className="shadcn-card">
                            <div className="shadcn-card-header">
                                <div className="shadcn-card-title">
                                    <Sparkles size={20} className="text-warning" />
                                    <span>მოთხოვნილი სერვისების განაწილება</span>
                                </div>
                            </div>
                            <div className="d-flex flex-column gap-3">
                                {Object.entries(stats.serviceMap).map(([key, item]) => {
                                    const pct = stats.total > 0 ? Math.round((item.count / stats.total) * 100) : 0;
                                    return (
                                        <div key={key}>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className="small text-dark fw-semibold">{item.label}</span>
                                                <span className="smallest text-muted">{item.count} მუნიციპ. ({pct}%)</span>
                                            </div>
                                            <div className="progress" style={{ height: '7px' }}>
                                                <div 
                                                    className={`progress-bar ${key === 'all_in_one' ? 'bg-success' : key === 'redesign_update' ? 'bg-primary' : key === 'cybersecurity_audit' ? 'bg-danger' : 'bg-info'}`} 
                                                    style={{ width: `${pct}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Autonomous AI Agent Automation Checklist */}
                        <div className="shadcn-card flex-grow-1" style={{ background: 'linear-gradient(180deg, rgba(99,102,241,0.04) 0%, #fff 100%)' }}>
                            <div className="shadcn-card-header">
                                <div className="shadcn-card-title">
                                    <Zap size={20} className="text-indigo" style={{ color: '#6366f1' }} />
                                    <span>ავტონომიური ვებ-სააგენტოს მზაობა (100%)</span>
                                </div>
                            </div>
                            <div className="d-flex flex-column gap-2 small">
                                <div className="d-flex align-items-center gap-2 p-2 rounded bg-light">
                                    <CheckCircle2 size={16} className="text-success" />
                                    <span>ავტომატური პერსონალიზებული შეთავაზებების გენერირება (AI)</span>
                                </div>
                                <div className="d-flex align-items-center gap-2 p-2 rounded bg-light">
                                    <CheckCircle2 size={16} className="text-success" />
                                    <span>ვებსაიტის ტექნიკური და კიბერ-აუდიტის შედეგების ჩაშენება</span>
                                </div>
                                <div className="d-flex align-items-center gap-2 p-2 rounded bg-light">
                                    <CheckCircle2 size={16} className="text-success" />
                                    <span>ინლაინ კალენდარი და Google Meet / Zoom შეხვედრების დაგეგმვა</span>
                                </div>
                                <div className="d-flex align-items-center gap-2 p-2 rounded bg-light">
                                    <CheckCircle2 size={16} className="text-success" />
                                    <span>დოკუმენტების და ხელშეკრულებების ელექტრონული მიბმა</span>
                                </div>
                                <div className="d-flex align-items-center gap-2 p-2 rounded bg-light">
                                    <CheckCircle2 size={16} className="text-success" />
                                    <span>მხარდაჭერის ტიკეტების ცენტრალიზებული სისტემა</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminMunicipalityAnalytics;
